import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AppShell, PageHeader } from "../components/AppShell";
import { CategoryChips } from "../components/CategoryChips";
import { CategoryBadge } from "../components/FoodCostBadge";
import { FAB } from "../components/FAB";
import { ShoppingListDialog } from "../components/ShoppingListDialog";
import { PrintableOrder } from "../components/PrintableOrder";
import { OrderIngredientButton } from "../components/OrderIngredientButton";
import { AssociateIngredientToSupplierButton } from "../components/AssociateSupplierButtons";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { INGREDIENT_CATEGORIES, catColor, euroPrecise } from "../lib/constants";
import { Search, X, Trash2, AlertTriangle, Sparkles, DownloadCloud, ShoppingBasket, Printer, Scale } from "lucide-react";
import { toast } from "sonner";

export default function Ingredients() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [resetOpen, setResetOpen] = useState(false);
    const [resetBusy, setResetBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [seedBusy, setSeedBusy] = useState(false);
    const [shoppingOpen, setShoppingOpen] = useState(false);
    const [shoppingCount, setShoppingCount] = useState(0);
    const [printOrder, setPrintOrder] = useState(null);
    const printRef = useRef(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    const load = () => {
        setLoading(true);
        api.get("/ingredients")
            .then(({ data }) => setItems(data))
            .finally(() => setLoading(false));
    };

    const refreshShoppingCount = async () => {
        try {
            const { data } = await api.get("/shopping-list");
            setShoppingCount(data.items?.length || 0);
        } catch {
            // silent
        }
    };

    useEffect(() => { load(); refreshShoppingCount(); }, []);

    const filtered = useMemo(() => {
        let r = items;
        if (cat) r = r.filter((i) => i.category === cat);
        if (q.trim()) {
            const s = q.toLowerCase();
            r = r.filter((i) => i.name.toLowerCase().includes(s));
        }
        return r;
    }, [items, q, cat]);

    const [fixPzOpen, setFixPzOpen] = useState(false);
    const [fixPzBusy, setFixPzBusy] = useState(false);
    const pzBadCount = useMemo(
        () => items.filter((i) => i.unit === "pz" && i.category !== "bevande").length,
        [items]
    );

    const fixPzUnits = async () => {
        setFixPzBusy(true);
        try {
            const { data } = await api.post("/ingredients/fix-pz-units");
            const skipped = (data.skipped_no_weight || []).map((x) => x.name);
            if (data.total_converted > 0) {
                toast.success(`${data.total_converted} ingredienti convertiti in peso`);
            } else {
                toast.info("Nessun ingrediente da convertire");
            }
            if (skipped.length > 0) {
                toast.warning(
                    `${skipped.length} ingredienti senza peso medio noto. Correggili a mano: ${skipped.slice(0, 4).join(", ")}${skipped.length > 4 ? "…" : ""}`,
                    { duration: 8000 }
                );
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setFixPzBusy(false);
            setFixPzOpen(false);
        }
    };

    const doReset = async () => {
        setResetBusy(true);
        try {
            const { data } = await api.delete("/ingredients");
            toast.success(`Cancellati ${data.deleted} ingredienti`);
            setResetOpen(false);
            setConfirmText("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setResetBusy(false);
        }
    };

    const loadSeed = async () => {
        setSeedBusy(true);
        try {
            const { data } = await api.post("/ingredients/seed-default");
            const added = data.added || 0;
            const updated = data.updated || 0;
            if (added > 0 && updated > 0) {
                toast.success(
                    `${added} nuovi ingredienti aggiunti, ${updated} aggiornati con i valori del catalogo`
                );
            } else if (added > 0) {
                toast.success(`Aggiunti ${added} ingredienti dal catalogo RENOVA360`);
            } else if (updated > 0) {
                toast.success(`${updated} ingredienti aggiornati con i valori del catalogo`);
            } else {
                toast.info("Nessun aggiornamento necessario");
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSeedBusy(false);
        }
    };

    return (
        <AppShell>
            <PageHeader
                eyebrow="Dispensa"
                title="Ingredienti"
                subtitle="Prezzi, scarti e fornitori di tutto ciò che entra in cucina."
                right={
                    <div className="hidden lg:flex items-center gap-2">
                        {pzBadCount > 0 && (
                            <button
                                onClick={() => setFixPzOpen(true)}
                                data-testid="ingredients-fix-pz-btn"
                                title="Converti a peso gli ingredienti registrati a pezzi"
                                className="inline-flex items-center gap-2 rounded-full border border-brand-warning/50 text-brand-warning bg-amber-50 hover:bg-amber-100 px-4 py-2 text-sm font-semibold transition"
                            >
                                <Scale size={14} /> Correggi {pzBadCount} a peso
                            </button>
                        )}
                        <button
                            onClick={() => setShoppingOpen(true)}
                            data-testid="ingredients-shopping-list-btn"
                            className={`relative inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                shoppingCount > 0
                                    ? "border-brand-warning text-white bg-brand-warning hover:opacity-90"
                                    : "border-brand-line text-brand-muted hover:border-brand-warning hover:text-brand-warning"
                            }`}
                        >
                            <ShoppingBasket size={14} /> Lista mancanti
                            {shoppingCount > 0 && (
                                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-brand-warning text-[11px] font-bold">
                                    {shoppingCount}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={loadSeed}
                            disabled={seedBusy}
                            data-testid="ingredients-load-seed-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 text-brand-navy bg-brand-gold/10 hover:bg-brand-gold/20 px-4 py-2 text-sm font-semibold transition disabled:opacity-60"
                        >
                            <DownloadCloud size={14} />
                            {seedBusy ? "Caricamento…" : "Carica tutti i nostri ingredienti"}
                        </button>
                        {items.length > 0 && (
                            <button
                                onClick={() => setResetOpen(true)}
                                data-testid="ingredients-reset-btn"
                                className="inline-flex items-center gap-2 rounded-full border border-brand-danger/40 text-brand-danger hover:bg-brand-danger/5 px-4 py-2 text-sm font-semibold transition"
                            >
                                <Trash2 size={14} /> Resetta tutti gli ingredienti
                            </button>
                        )}
                    </div>
                }
            />

            <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input
                        data-testid="ingredients-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca un ingrediente…"
                        className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold transition"
                    />
                    {q && (
                        <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted">
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            <CategoryChips value={cat} onChange={setCat} categories={INGREDIENT_CATEGORIES} allLabel="Tutti" />

            <div className="mt-6 text-sm text-brand-muted">
                {loading ? "Caricamento…" : `${filtered.length} ingredienti`}
            </div>

            <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="ingredients-list">
                {filtered.map((i) => (
                    <div
                        key={i.id}
                        data-testid={`ingredient-row-${i.id}`}
                        className="bg-paper rounded-xl border border-brand-line shadow-card hover:shadow-cardHover hover:border-brand-gold/40 transition-all duration-300 flex items-center gap-3 pr-3 group"
                    >
                        <button
                            type="button"
                            onClick={() => navigate(`/ingredients/${i.id}`)}
                            className="flex-1 min-w-0 flex items-center gap-4 text-left"
                            aria-label={`Apri dettaglio ${i.name}`}
                        >
                            <span
                                className="w-1.5 h-14 rounded-r-full shrink-0"
                                style={{ background: catColor(i.category) }}
                            />
                            <div className="flex-1 min-w-0 py-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-semibold text-brand-ink text-[15px] truncate">{i.name}</div>
                                    <CategoryBadge category={i.category} />
                                    {Number(i.waste_pct) > 0 && (
                                        <span className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">
                                            scarto {Number(i.waste_pct).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                                {i.supplier && (
                                    <div className="text-[11px] text-brand-muted mt-0.5 truncate">{i.supplier}</div>
                                )}
                            </div>
                            <div className="text-right py-3">
                                <div className="font-display text-xl text-brand-ink leading-none">
                                    {euroPrecise(i.cost_per_unit)}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-brand-muted mt-1 font-semibold">
                                    per {i.unit}
                                </div>
                            </div>
                        </button>
                        <AssociateIngredientToSupplierButton ingredient={i} compact />
                        <OrderIngredientButton ingredient={i} compact />
                    </div>
                ))}
                {!loading && filtered.length === 0 && items.length === 0 && (
                    <div
                        className="col-span-full bg-paper rounded-xl border border-dashed border-brand-line p-10 text-center"
                        data-testid="ingredients-empty-state"
                    >
                        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-brand-gold/15 text-brand-gold mb-3">
                            <Sparkles size={20} />
                        </div>
                        <div className="font-display text-2xl text-brand-ink">
                            La tua dispensa è vuota
                        </div>
                        <p className="mt-1 text-sm text-brand-muted max-w-md mx-auto">
                            Carica i 267 ingredienti italiani pre-calibrati di RENOVA360 oppure
                            crea il tuo primo ingrediente.
                        </p>
                        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
                            <button
                                onClick={loadSeed}
                                disabled={seedBusy}
                                data-testid="ingredients-load-seed-empty-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-5 py-2.5 text-sm font-bold hover:bg-brand-goldDark transition disabled:opacity-60"
                            >
                                <DownloadCloud size={16} />
                                {seedBusy ? "Caricamento…" : "Carica tutti i nostri ingredienti"}
                            </button>
                            <button
                                onClick={() => navigate("/ingredients/new")}
                                className="inline-flex items-center gap-2 rounded-full border border-brand-line text-brand-ink px-5 py-2.5 text-sm font-semibold hover:border-brand-navy transition"
                            >
                                Crea ingrediente
                            </button>
                        </div>
                    </div>
                )}
                {!loading && filtered.length === 0 && items.length > 0 && (
                    <div className="col-span-full bg-paper rounded-xl border border-dashed border-brand-line p-10 text-center text-sm text-brand-muted">
                        Nessun ingrediente trovato. Prova a togliere i filtri o aggiungine uno nuovo.
                    </div>
                )}
            </div>

            <FAB onClick={() => navigate("/ingredients/new")} label="Nuovo ingrediente" testid="ingredients-fab" />

            <div className="lg:hidden mt-8 flex flex-col items-center gap-3">
                <button
                    onClick={() => setShoppingOpen(true)}
                    data-testid="ingredients-shopping-list-btn-mobile"
                    className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2 text-[12px] font-bold uppercase tracking-wider transition ${
                        shoppingCount > 0
                            ? "bg-brand-warning text-white"
                            : "border border-brand-line text-brand-muted"
                    }`}
                >
                    <ShoppingBasket size={14} /> Lista mancanti
                    {shoppingCount > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-brand-warning text-[11px] font-bold">
                            {shoppingCount}
                        </span>
                    )}
                </button>
                <button
                    onClick={loadSeed}
                    disabled={seedBusy}
                    data-testid="ingredients-load-seed-btn-mobile"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 text-brand-navy bg-brand-gold/10 hover:bg-brand-gold/20 px-5 py-2 text-[12px] font-bold uppercase tracking-wider transition disabled:opacity-60"
                >
                    <DownloadCloud size={14} />
                    {seedBusy ? "Caricamento…" : "Carica tutti i nostri ingredienti"}
                </button>
                {items.length > 0 && (
                    <button
                        onClick={() => setResetOpen(true)}
                        data-testid="ingredients-reset-btn-mobile"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-danger/40 text-brand-danger px-5 py-2 text-[12px] font-bold uppercase tracking-wider hover:bg-brand-danger/5 transition"
                    >
                        <Trash2 size={14} /> Resetta tutti gli ingredienti
                    </button>
                )}
            </div>

            {resetOpen && (
                <div
                    className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-5"
                    onClick={() => !resetBusy && setResetOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-card overflow-hidden"
                        data-testid="reset-ingredients-dialog"
                    >
                        <div className="bg-brand-danger/8 border-b border-brand-danger/20 p-5 flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-brand-danger/15 text-brand-danger flex items-center justify-center shrink-0">
                                <AlertTriangle size={18} />
                            </div>
                            <div>
                                <div className="font-display text-lg text-brand-ink">
                                    Resettare tutti gli ingredienti?
                                </div>
                                <p className="mt-1 text-[13px] text-brand-muted">
                                    Stai per cancellare <strong>{items.length} ingredienti</strong> dalla
                                    tua dispensa. Le ricette resteranno, ma i food cost diventeranno 0
                                    finché non rimetterai gli ingredienti.
                                </p>
                            </div>
                        </div>
                        <div className="p-5 space-y-3">
                            <label className="block text-[11px] uppercase tracking-[0.16em] text-brand-muted font-semibold">
                                Per confermare scrivi <span className="text-brand-danger font-bold">RESETTA</span>
                            </label>
                            <input
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                data-testid="reset-confirm-input"
                                placeholder="RESETTA"
                                className="w-full px-3 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-danger"
                            />
                            <div className="flex items-center justify-end gap-2 pt-2">
                                <button
                                    onClick={() => { setResetOpen(false); setConfirmText(""); }}
                                    disabled={resetBusy}
                                    data-testid="reset-cancel-btn"
                                    className="px-4 py-2 rounded-full border border-brand-line text-sm font-semibold text-brand-muted hover:bg-brand-cream"
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={doReset}
                                    disabled={resetBusy || confirmText.trim().toUpperCase() !== "RESETTA"}
                                    data-testid="reset-confirm-btn"
                                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-danger text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    <Trash2 size={14} />
                                    {resetBusy ? "Cancellazione…" : "Resetta tutto"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ShoppingListDialog
                open={shoppingOpen}
                onOpenChange={setShoppingOpen}
                onChanged={refreshShoppingCount}
                onPrintReady={(orderDoc) => {
                    setPrintOrder(orderDoc);
                    // Aspetta il commit DOM, poi avvia la stampa.
                    // `afterprint` ripulisce lo stato a fine stampa.
                    setTimeout(() => {
                        const handler = () => {
                            setPrintOrder(null);
                            window.removeEventListener("afterprint", handler);
                        };
                        window.addEventListener("afterprint", handler);
                        window.print();
                    }, 350);
                }}
            />

            {/* Portale di stampa: renderizzato direttamente sul body così non
                eredita position/overflow dall'AppShell. Le regole CSS
                @media print nascondono tutto il resto del documento. */}
            {printOrder && createPortal(
                <div className="print-portal" ref={printRef}>
                    <PrintableOrder result={printOrder} user={user} />
                </div>,
                document.body
            )}

            {fixPzOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
                    onClick={() => !fixPzBusy && setFixPzOpen(false)}
                    data-testid="fix-pz-dialog"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
                    >
                        <div className="px-6 py-5 border-b border-brand-line">
                            <div className="h-11 w-11 rounded-full bg-amber-100 text-brand-warning flex items-center justify-center mb-3">
                                <Scale size={20} />
                            </div>
                            <h3 className="font-display text-2xl text-brand-ink">
                                Convertire {pzBadCount} ingredienti a peso?
                            </h3>
                            <p className="mt-2 text-sm text-brand-muted">
                                Il sistema userà <strong>pesi medi realistici</strong>
                                (uovo 55g, limone 100g, avocado 180g…) per convertire gli
                                ingredienti da "pezzi" a grammi, ricalcolando il costo/g.
                                Le ricette che li usano saranno aggiornate automaticamente.
                            </p>
                            <div className="mt-3 text-[12px] text-brand-ink/70 bg-brand-cream rounded-lg px-3 py-2 border border-brand-line/60">
                                Gli ingredienti senza un peso medio noto non verranno toccati —
                                riceverai la lista per correggerli a mano.
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setFixPzOpen(false)}
                                disabled={fixPzBusy}
                                data-testid="fix-pz-cancel"
                                className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition disabled:opacity-50"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={fixPzUnits}
                                disabled={fixPzBusy}
                                data-testid="fix-pz-confirm"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-warning text-white text-sm font-bold hover:opacity-90 disabled:opacity-60 transition"
                            >
                                <Scale size={14} /> {fixPzBusy ? "Conversione…" : "Sì, converti"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}
